import { Citation, ModelType, ParsedLegifranceLink } from '@livv/models';
import { Box, Button, Typography } from '@mui/material';
import { query, Query, where } from 'firebase/firestore';
import useTranslation from 'next-translate/useTranslation';
import { FC, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useCollectionDataOnce } from 'react-firebase-hooks/firestore';
import CitationSearch from '@components/Citations/CitationSearch';
import FullPageLoader from '@components/Loader/FullPageLoader';
import PreviewCard from '@components/previews/PreviewCard';
import SideContentCard from '@components/SideContentCard';
import SideModal from '@components/SideModal';
import { citationsCollection } from '@utils/collections';

const labelSx = {
    color: 'grey.100',
    fontWeight: 'medium',
    mb: 1,
    mt: 0,
    mx: 0,
};

interface CitationsProps {
    codeArticleCitations?: ParsedLegifranceLink[];
    documentId: string;
    modelType: ModelType;
    title: string;
}

const Citations: FC<CitationsProps> = ({ codeArticleCitations, documentId, modelType, title }) => {
    const { t } = useTranslation('common');
    const [isSideModalOpen, setIsSideModalOpen] = useState(false);
    const [internalCitations = [], loading, error] = useCollectionDataOnce<Citation>(
        query(citationsCollection, where('target.id', '==', documentId)) as Query<Citation>,
    );
    const [searchValue, setSearchValue] = useState('');

    useErrorHandler(error);

    if (loading) {
        return <FullPageLoader height="50px" embedded />;
    }

    const citations = [...internalCitations, ...(codeArticleCitations || [])];

    if (!citations.length) {
        return null;
    }

    const lowerCasedSearchValue = searchValue.toLowerCase();
    const filteredCodeArticleCitations =
        codeArticleCitations?.filter(({ textTitle }) =>
            textTitle.toLowerCase().includes(lowerCasedSearchValue),
        ) || [];
    const filteredInternalCitations =
        internalCitations?.filter(({ source }) => source.title.includes(lowerCasedSearchValue)) ||
        [];

    return (
        <>
            <SideContentCard
                sx={{ mb: 1.5 }}
                title={t('citations.preview.title', { count: citations.length })}
            >
                <Button
                    onClick={() => setIsSideModalOpen(true)}
                    sx={{ bgcolor: 'white', fontSize: 'inherit' }}
                    variant="text"
                >
                    {t('citations.preview.buttonLabel', { count: citations.length })}
                </Button>
            </SideContentCard>
            <SideModal onClose={() => setIsSideModalOpen(false)} open={isSideModalOpen}>
                <Box sx={{ mr: 2 }}>
                    <Typography
                        dangerouslySetInnerHTML={{
                            __html: t(`citations.search.label`, {
                                modelTypePrefixed: t(`modelType:PrefixedSingular.${modelType}`),
                                title,
                            }),
                        }}
                        sx={labelSx}
                        variant="body1"
                    />
                    <CitationSearch setSearchValue={setSearchValue} />
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        {filteredCodeArticleCitations.map(
                            ({ articleId: versionId, isInFirestore, textTitle }) => (
                                <PreviewCard
                                    key={`preview_${versionId}`}
                                    articleData={[versionId, textTitle, isInFirestore]}
                                    title={textTitle}
                                    isExternal
                                />
                            ),
                        )}
                        {filteredInternalCitations.map(({ source }) => (
                            <PreviewCard
                                key={`preview_${source.id}`}
                                citationData={source}
                                modelType={source.type}
                                title={source.title}
                            />
                        ))}
                    </Box>
                </Box>
            </SideModal>
        </>
    );
};

export default Citations;
